import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import CircularProgress from "@material-ui/core/CircularProgress";
import Tooltip from "@material-ui/core/Tooltip";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import A_SecuredAction from "../../../Atoms/Security/A_SecuredAction";
import { pageSecurityActions } from "../../../../../Utils/Helpers/pageSecurity";
import InfoIcon from "@material-ui/icons/Info";
import A_TextField from "../../../Atoms/Inputs/TextFields/A_TextField";
import A_IconButton from "../../../Atoms/Inputs/Buttons/A_IconButton";
import {
  canAddScenario,
  canRemoveScenario,
  scenarioCountHint,
  formatPercent,
} from "../../../../../domain/provision/provision.rules";
import { MIN_SCENARIOS } from "../../../../../domain/provision/provision.types";
import { api } from "./Provision.helper";
import useStyles from "./Provision.styles";

/**
 * The only row fields that feed the weighted Provision Outcome the API calculates. A Description
 * edit is still persisted, but it cannot move the outcome, so it must not trigger the refresh
 * that pulls the recalculated value back (DFXX10) — see handleBlur.
 */
const CALCULATION_FIELDS = ["provisionPct", "weightingPct"];

/**
 * Scenarios (claude-code-UI-prompt.md §Screen structure 4, DFXX9-10): table of 2-5 weighted
 * scenarios with sequential labels; add disabled at 5, remove hidden at 2; Provision/Weighting
 * accept 0.00-100.00%. Provision Outcome is the auto-calculated, read-only value the API
 * returns. Each row mutates immediately via its own endpoint — see provisionApi.js — rather than
 * batching into the Provision Details "Save", matching the contract's separate scenario paths.
 */
const ScenariosSection = ({ loanId, scenarios, provisionOutcomePct, editable, onChange }) => {
  const classes = useStyles();
  const [rowBusy, setRowBusy] = useState(null);
  const [error, setError] = useState(null);
  // Per-row keystrokes are only ever persisted on blur (handleBlur), but that must not depend on
  // `scenarios` having already round-tripped through the parent's onChange by the time blur
  // fires — a parent that doesn't feed the updated array straight back into props (or a test
  // rendering this component standalone) would otherwise save whatever was on `scenarios` before
  // the edit. Track the latest typed value per (scenario, field) here instead, independent of
  // the scenarios prop, and clear it once persisted.
  const pendingEdits = useRef({});

  const updateRowField = (scenario, field, value) => {
    pendingEdits.current[scenario.id] = { ...pendingEdits.current[scenario.id], [field]: value };
    // Optimistic local edit for any consumer that re-renders from this — the authoritative
    // values still come from handleBlur's own listScenarios refresh below.
    onChange(
      scenarios.map((s) => (s.id === scenario.id ? { ...s, [field]: value } : s)),
      provisionOutcomePct
    );
  };

  /** Drops the fields we just persisted from the pending map, leaving any typed since untouched. */
  const clearPersistedEdits = (scenarioId, persisted) => {
    const current = pendingEdits.current[scenarioId] || {};
    Object.keys(persisted).forEach((field) => {
      if (current[field] === persisted[field]) delete current[field];
    });
    if (Object.keys(current).length === 0) delete pendingEdits.current[scenarioId];
    else pendingEdits.current[scenarioId] = current;
  };

  const handleBlur = async (scenario) => {
    const pending = pendingEdits.current[scenario.id];
    // A focus-out that changed nothing is not an edit: no PUT, and above all no outcome refresh.
    if (!pending || Object.keys(pending).length === 0) return;
    const description = "description" in pending ? pending.description : scenario.description;
    const provisionPct = "provisionPct" in pending ? pending.provisionPct : scenario.provisionPct;
    const weightingPct = "weightingPct" in pending ? pending.weightingPct : scenario.weightingPct;
    // Provision, Weighting and the add/remove Actions are the only things that can change the
    // weighted outcome, so only they re-read it. A Description edit saves silently — no row
    // spinner (which would disable the Provision field the user just tabbed into) and no
    // recalculation, which previously fired on every description keystroke's blur.
    const affectsCalculation = CALCULATION_FIELDS.some((field) => field in pending);
    if (affectsCalculation) setRowBusy(scenario.id);
    setError(null);
    try {
      await api.updateScenario(loanId, scenario.id, {
        description,
        provisionPct: provisionPct === "" ? null : provisionPct,
        weightingPct: weightingPct === "" ? null : weightingPct,
      });
      clearPersistedEdits(scenario.id, pending);
      if (!affectsCalculation) return;
      const refreshed = await api.listScenarios(loanId);
      onChange(refreshed.scenarios, refreshed.provisionOutcomePct);
    } catch (err) {
      setError(err.message || "Couldn't save that scenario. Please try again.");
    } finally {
      if (affectsCalculation) setRowBusy(null);
    }
  };

  const handleAdd = async () => {
    if (!canAddScenario(scenarios)) return;
    setRowBusy("adding");
    setError(null);
    try {
      await api.addScenario(loanId, { description: "", provisionPct: null, weightingPct: null });
      const refreshed = await api.listScenarios(loanId);
      onChange(refreshed.scenarios, refreshed.provisionOutcomePct);
    } catch (err) {
      setError(err.message || "Couldn't add a scenario. Please try again.");
    } finally {
      setRowBusy(null);
    }
  };

  const handleRemove = async (scenario) => {
    if (!canRemoveScenario(scenarios)) return;
    setRowBusy(scenario.id);
    setError(null);
    try {
      await api.deleteScenario(loanId, scenario.id);
      const refreshed = await api.listScenarios(loanId);
      onChange(refreshed.scenarios, refreshed.provisionOutcomePct);
    } catch (err) {
      setError(err.message || "Couldn't remove that scenario. Please try again.");
    } finally {
      setRowBusy(null);
    }
  };

  const sorted = [...scenarios].sort((a, b) => (a.sequence || 0) - (b.sequence || 0));

  return (
    <div className={classes.card} style={{ marginBottom: 0 }}>
      <div className={classes.cardHeader}>
        <span className={classes.cardHeaderTitle}>Scenarios</span>
      </div>
      <div className={classes.cardBody}>
        <p className={classes.subtle} style={{ marginTop: 0 }}>
          Record 2-5 weighted scenarios. Provision outcome is auto-calculated from the weightings.
        </p>
        {error && (
          <div className={classes.validationSummary} style={{ marginBottom: 12 }}>
            {error}
          </div>
        )}
        <Table size="small" className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell style={{ width: 100 }}>Scenario</TableCell>
              <TableCell>Description</TableCell>
              <TableCell className={classes.numericCell} style={{ width: 128 }}>
                Provision
              </TableCell>
              <TableCell className={classes.numericCell} style={{ width: 128 }}>
                Weighting
              </TableCell>
              <TableCell style={{ width: 56 }} align="center">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((scenario) => (
              <TableRow key={scenario.id}>
                <TableCell className={classes.subtle} style={{ whiteSpace: "nowrap", fontWeight: 600 }}>
                  Scenario {scenario.sequence}
                </TableCell>
                <TableCell>
                  <A_TextField
                    id={`scenario-description-${scenario.id}`}
                    label=""
                    inputProps={{ placeholder: "Enter description" }}
                    defaultValue={scenario.description || ""}
                    onChange={(e) => updateRowField(scenario, "description", e.target.value)}
                    onBlur={() => handleBlur(scenario)}
                    disabled={!editable || rowBusy === scenario.id}
                    variant="outlined"
                    size="small"
                    fullWidth
                  />
                </TableCell>
                <TableCell className={classes.numericCell}>
                  {/* type="text" (not "number") — a native number input's up/down spinner has
                      nowhere to go in this narrow column and collides with the value/adornment
                      (see mockup: plain text-styled "0.00%", no spinner). isValidPercent still
                      enforces the 0-100/2dp rule; the server is authoritative regardless.
                      classes.percentField is what makes a three-digit "100.00" fit — see the
                      note on it in Provision.styles.js. */}
                  <A_TextField
                    className={classes.percentField}
                    id={`scenario-provision-${scenario.id}`}
                    label=""
                    type="text"
                    adornment={{ position: "end", value: "%" }}
                    defaultValue={scenario.provisionPct ?? ""}
                    onChange={(e) => updateRowField(scenario, "provisionPct", e.target.value)}
                    onBlur={() => handleBlur(scenario)}
                    disabled={!editable || rowBusy === scenario.id}
                    inputProps={{ inputMode: "decimal", placeholder: "0.00" }}
                    variant="outlined"
                    size="small"
                    fullWidth
                  />
                </TableCell>
                <TableCell className={classes.numericCell}>
                  <A_TextField
                    className={classes.percentField}
                    id={`scenario-weighting-${scenario.id}`}
                    label=""
                    type="text"
                    adornment={{ position: "end", value: "%" }}
                    defaultValue={scenario.weightingPct ?? ""}
                    onChange={(e) => updateRowField(scenario, "weightingPct", e.target.value)}
                    onBlur={() => handleBlur(scenario)}
                    disabled={!editable || rowBusy === scenario.id}
                    inputProps={{ inputMode: "decimal", placeholder: "0.00" }}
                    variant="outlined"
                    size="small"
                    fullWidth
                  />
                </TableCell>
                <TableCell align="center">
                  {rowBusy === scenario.id ? (
                    <CircularProgress size={16} />
                  ) : (
                    // Rendered disabled rather than hidden when the 2-scenario floor is reached:
                    // omitting it left the Actions column visibly empty on every loan sitting at
                    // the minimum, which reads as a broken control rather than a rule. The rule
                    // itself is unchanged — canRemoveScenario still gates it, and handleRemove
                    // re-checks it — the tooltip just says why it can't be used.
                    <Tooltip
                      title={
                        canRemoveScenario(scenarios)
                          ? `Remove Scenario ${scenario.sequence}`
                          : `At least ${MIN_SCENARIOS} scenarios are required`
                      }
                    >
                      <span>
                        <A_SecuredAction
                          permission={pageSecurityActions.DELETE}
                        >
                          <A_IconButton
                            size="small"
                            icon={<DeleteIcon fontSize="small" />}
                            ariaLabel={`Remove Scenario ${scenario.sequence}`}
                            onClick={() => handleRemove(scenario)}
                            disabled={
                              !editable || !canRemoveScenario(scenarios)
                            }
                          />
                        </A_SecuredAction>
                      </span>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 14 }}>
          {rowBusy === "adding" ? (
            <CircularProgress size={16} />
          ) : (
            <A_SecuredAction permission={pageSecurityActions.CREATE}>
              <button className={classes.linkAction} onClick={handleAdd} disabled={!editable || !canAddScenario(scenarios)}>
                <AddIcon fontSize="small" /> Add Scenario
              </button>
            </A_SecuredAction>
          )}
          <span className={classes.hint}>{scenarioCountHint(scenarios.length)}</span>
        </div>
        <div className={classes.outcome}>
          <span className={classes.outcomeLabel}>
            Provision Outcome <span className={classes.calcBadge}>Auto</span>
          </span>
          <span className={classes.outcomeValue}>{formatPercent(provisionOutcomePct) || "0.00"}%</span>
        </div>
        <div className={classes.hint} style={{ marginTop: 10 }}>
          <InfoIcon fontSize="inherit" /> Changes to scenarios save immediately.
        </div>
      </div>
    </div>
  );
};

ScenariosSection.propTypes = {
  loanId: PropTypes.string.isRequired,
  scenarios: PropTypes.array.isRequired,
  provisionOutcomePct: PropTypes.number,
  editable: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

export default ScenariosSection;
